import type { ILanguageResources, ITranslationModule } from '../../../../interfaces';
import { interpolate } from '../../../../global';

class Id {
  private constructor() {}
  public static readonly prefix = 'UI.scripts.dealer-franchisee.quotes.views.QuoteDealerPriceAdjustmentDialog.';

  public static readonly levelPriceAdjustmentTitle = `${Id.prefix}levelPriceAdjustmentTitle`;
  public static readonly taxLabel = `${Id.prefix}taxLabel`;
  public static readonly taxGlossaryLabel = `${Id.prefix}taxGlossaryLabel`;
  public static readonly adjustmentTax = `${Id.prefix}adjustmentTax`;
  public static readonly taxablePriceAdjustment = `${Id.prefix}taxablePriceAdjustment`;
  public static readonly selectTaxRate = `${Id.prefix}selectTaxRate`;
  public static readonly itemTax = `${Id.prefix}itemTax`;
  public static readonly zeroRatedItems = `${Id.prefix}zeroRatedItems`;
  public static readonly taxedItems = `${Id.prefix}taxedItems`;
  public static readonly items = `${Id.prefix}items`;
  public static readonly noAdjustmentApplied = `${Id.prefix}noAdjustmentApplied`;
  public static readonly adjustmentDetails = `${Id.prefix}adjustmentDetails`;
  public static readonly titleLabel = `${Id.prefix}titleLabel`;
  public static readonly franchiseeQuotePriceAdjustmentGlossary = `${Id.prefix}franchiseeQuotePriceAdjustmentGlossary`;
  public static readonly descriptionLabel = `${Id.prefix}descriptionLabel`;
  public static readonly quotePriceAdjustmentGlossary = `${Id.prefix}quotePriceAdjustmentGlossary`;
  public static readonly commentLabel = `${Id.prefix}commentLabel`;
  public static readonly addCommentIfRequired = `${Id.prefix}addCommentIfRequired`;
  public static readonly quotePriceGlossary = `${Id.prefix}quotePriceGlossary`;
  public static readonly adjustment = `${Id.prefix}adjustment`;
  public static readonly itemsTotal = `${Id.prefix}itemsTotal`;
  public static readonly quoteTotalGlossary = `${Id.prefix}quoteTotalGlossary`;
  public static readonly reset = `${Id.prefix}reset`;
}

export class LangQuoteDealerPriceAdjustmentDialog {
  private constructor() {}

  public static levelPriceAdjustmentTitle(): string {
    return interpolate(Id.levelPriceAdjustmentTitle);
  }
  public static taxLabel(params: { name: string; rate: number }): string {
    return interpolate(Id.taxLabel, params);
  }
  public static taxGlossaryLabel(params: { percentage: string }): string {
    return interpolate(Id.taxGlossaryLabel, params);
  }
  public static adjustmentTax(): string {
    return interpolate(Id.adjustmentTax);
  }
  public static taxablePriceAdjustment(): string {
    return interpolate(Id.taxablePriceAdjustment);
  }
  public static selectTaxRate(): string {
    return interpolate(Id.selectTaxRate);
  }
  public static itemTax(): string {
    return interpolate(Id.itemTax);
  }
  public static zeroRatedItems(): string {
    return interpolate(Id.zeroRatedItems);
  }
  public static taxedItems(): string {
    return interpolate(Id.taxedItems);
  }
  public static items(): string {
    return interpolate(Id.items);
  }
  public static noAdjustmentApplied(): string {
    return interpolate(Id.noAdjustmentApplied);
  }
  public static adjustmentDetails(): string {
    return interpolate(Id.adjustmentDetails);
  }
  public static titleLabel(): string {
    return interpolate(Id.titleLabel);
  }
  public static franchiseeQuotePriceAdjustmentGlossary(): string {
    return interpolate(Id.franchiseeQuotePriceAdjustmentGlossary);
  }
  public static descriptionLabel(): string {
    return interpolate(Id.descriptionLabel);
  }
  public static quotePriceAdjustmentGlossary(): string {
    return interpolate(Id.quotePriceAdjustmentGlossary);
  }
  public static commentLabel(): string {
    return interpolate(Id.commentLabel);
  }
  public static addCommentIfRequired(): string {
    return interpolate(Id.addCommentIfRequired);
  }
  public static quotePriceGlossary(): string {
    return interpolate(Id.quotePriceGlossary);
  }
  public static adjustment(): string {
    return interpolate(Id.adjustment);
  }
  public static itemsTotal(): string {
    return interpolate(Id.itemsTotal);
  }
  public static quoteTotalGlossary(): string {
    return interpolate(Id.quoteTotalGlossary);
  }
  public static reset(): string {
    return interpolate(Id.reset);
  }
}

export class QuoteDealerPriceAdjustmentDialogModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(
      Id.levelPriceAdjustmentTitle,
      "{{glossary 'franchisee'}} {{glossary 'quote'}} Level Price Adjustment",
      'Title for the price adjustment dialog, using glossary terms for franchisee and quote'
    );
    register.add(Id.taxLabel, '{{name}} {{rate}}%', 'Tax label with name and rate');
    register.add(
      Id.taxGlossaryLabel,
      "{{glossary 'tax'}} ({{percentage}}%)",
      'Tax label using glossary and percentage'
    );
    register.add(Id.adjustmentTax, 'Adjustment Tax', 'Label for adjustment tax');
    register.add(Id.taxablePriceAdjustment, 'Taxable Price Adjustment', 'Label for taxable price adjustment switch');
    register.add(Id.selectTaxRate, 'Select Tax Rate', 'Placeholder for selecting a tax rate');
    register.add(Id.itemTax, 'Item Tax', 'Label for item tax');
    register.add(Id.zeroRatedItems, 'Zero Rated Items', 'Label for non-taxable items');
    register.add(Id.taxedItems, 'Taxed Items', 'Label for taxable items');
    register.add(Id.items, 'Items', 'Label for items');
    register.add(Id.noAdjustmentApplied, 'No Adjustment Applied', 'Badge for no adjustment applied');
    register.add(Id.adjustmentDetails, 'Adjustment Details', 'Section header for adjustment details');
    register.add(Id.titleLabel, 'Title', 'Label for adjustment title');
    register.add(
      Id.franchiseeQuotePriceAdjustmentGlossary,
      "{{glossary 'franchisee-quote-price-adjustment'}}",
      'Glossary for franchisee quote price adjustment'
    );
    register.add(Id.descriptionLabel, 'Description', 'Label for adjustment description');
    register.add(
      Id.quotePriceAdjustmentGlossary,
      "{{glossary 'quote'}} price adjustment",
      'Glossary for quote price adjustment'
    );
    register.add(Id.commentLabel, 'Comment', 'Label for comment');
    register.add(Id.addCommentIfRequired, 'Add comment if required', 'Placeholder for comment');
    register.add(Id.quotePriceGlossary, "{{glossary 'quote'}} Price", 'Glossary for quote price');
    register.add(Id.adjustment, 'Adjustment', 'Label for adjustment');
    register.add(Id.itemsTotal, 'Items Total', 'Label for items total');
    register.add(Id.quoteTotalGlossary, "{{glossary 'quote'}} Total", 'Glossary for quote total');
    register.add(Id.reset, 'Reset', 'Button label for reset');
  }
}
